import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const BUCKET = "documentos_kargo";
const SIGNED_URL_EXPIRY = 3600; // 1 hour

/**
 * Sanitises any archivo_url variant into the bare object path
 * that createSignedUrl expects (no bucket prefix, no full URL).
 */
function sanitizePath(raw: string, bucket: string): string {
  let p = raw.trim();

  // 1. Full Supabase URL → extract everything after /object/public|sign/<bucket>/
  const urlRe = new RegExp(`/storage/v1/object/(?:public|sign)/${bucket}/`, "i");
  const urlMatch = p.search(urlRe);
  if (urlMatch !== -1) {
    p = p.slice(p.indexOf(bucket + "/", urlMatch) + bucket.length + 1);
  }

  // 2. Starts with "<bucket>/" → strip it
  if (p.startsWith(`${bucket}/`)) {
    p = p.slice(bucket.length + 1);
  }

  // 3. Remove any leading slash
  p = p.replace(/^\/+/, "");

  console.log("Ruta sanitizada para Signed URL:", p);
  return p;
}

/**
 * Resolves a storage path or full URL into a secure signed URL.
 */
export function useSignedUrl(url: string | null | undefined, bucket = BUCKET) {
  const [secureUrl, setSecureUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) {
      setSecureUrl(null);
      setLoading(false);
      return;
    }

    // Legacy full public URLs that are NOT Supabase storage → pass through
    if (url.startsWith("http") && !url.includes("/storage/v1/object/")) {
      setSecureUrl(url);
      setLoading(false);
      return;
    }

    // Sanitise & generate signed URL
    const cleanPath = sanitizePath(url, bucket);
    if (!cleanPath) {
      setSecureUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    supabase.storage
      .from(bucket)
      .createSignedUrl(cleanPath, SIGNED_URL_EXPIRY)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data?.signedUrl) {
          console.error("Failed to generate signed URL:", error);
          setSecureUrl(null);
        } else {
          setSecureUrl(data.signedUrl);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [url, bucket]);

  return { secureUrl, loading };
}
